import { useState, useCallback, useEffect, useRef } from 'react';
import api from '../../../../../config/axiosConfig';
import { API } from '../../../../../utils/apiRoutes';

const DEFAULT_PAGE_SIZE = 15;

/**
 * Operation history of ONE enrollment, paginated and filtered server-side.
 * `page` is 0-based (TablePagination); the API's `page` is 1-based.
 * `statusFilter` is the API `status` param ('' = no filter, e.g. '1,10').
 */
export function useEnrollmentOperations(selectedEnrollmentId) {
    const [operations, setOperations] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [statusFilter, setStatusFilterState] = useState('');
    const requestIdRef = useRef(0);

    // A different enrollment starts unfiltered on the first page (reset during
    // render so the fetch effect below runs once, with the reset values).
    const [trackedEnrollmentId, setTrackedEnrollmentId] = useState(selectedEnrollmentId);
    if (trackedEnrollmentId !== selectedEnrollmentId) {
        setTrackedEnrollmentId(selectedEnrollmentId);
        setPage(0);
        setStatusFilterState('');
    }

    const fetchOperations = useCallback(async (enrollmentId) => {
        const requestId = ++requestIdRef.current;
        if (!enrollmentId) {
            setOperations([]);
            setTotalCount(0);
            return;
        }

        setLoading(true);
        setError(null);

        const params = { student_and_course: enrollmentId, page: page + 1, page_size: pageSize };
        if (statusFilter) params.status = statusFilter;

        try {
            const response = await api.get(API.studentOperations, { params });
            if (requestId !== requestIdRef.current) return;
            const data = response.data;
            if (Array.isArray(data)) {
                setOperations(data);
                setTotalCount(data.length);
            } else if (data && Array.isArray(data.results)) {
                setOperations(data.results);
                setTotalCount(Number(data.count) || data.results.length);
            } else {
                setOperations([]);
                setTotalCount(0);
            }
        } catch (err) {
            if (requestId !== requestIdRef.current) return;
            // Page no longer exists (e.g. its last row was deleted): step back one page.
            if (err?.response?.status === 404 && page > 0) {
                setPage(page - 1);
                return;
            }
            setError(err.message);
            setOperations([]);
            setTotalCount(0);
        } finally {
            if (requestId === requestIdRef.current) setLoading(false);
        }
    }, [page, pageSize, statusFilter]);

    // Fetch when the enrollment, page, page size or filter changes
    useEffect(() => {
        fetchOperations(selectedEnrollmentId);
    }, [selectedEnrollmentId, fetchOperations]);

    const setStatusFilter = useCallback((value) => {
        setStatusFilterState(value || '');
        setPage(0);
    }, []);

    const changePageSize = useCallback((value) => {
        setPageSize(Number(value) || DEFAULT_PAGE_SIZE);
        setPage(0);
    }, []);

    const deleteOperation = useCallback(async (operationId) => {
        try {
            await api.delete(`${API.studentOperations}${operationId}/`);
            // Optimistic remove
            setOperations(prev => Array.isArray(prev) ? prev.filter(op => op.id !== operationId) : prev);
            setTotalCount(prev => Math.max(prev - 1, 0));
            return true;
        } catch (err) {
            setError(err.message);
            return false;
        }
    }, []);

    return {
        operations,
        totalCount,
        loading,
        error,
        page,
        pageSize,
        statusFilter,
        setPage,
        setPageSize: changePageSize,
        setStatusFilter,
        fetchOperations,
        deleteOperation
    };
}
